/**
 * Security · Recent changes — compact table that renders the newest
 * site-scoped security actions (who / when / what). Lives beneath the
 * trusted-addresses table on the Blocking tab.
 *
 * 25 rows per page, newest first; the same Pager style AddressTables
 * uses. Empty state is "No changes yet."
 */

import { useCallback, useEffect, useState } from 'react';

import type {
  SecurityActionEntry,
  SecurityActionsListResponse,
} from '@sill/types/api';

import { useOptionalHttp } from '../../api/http-context.js';
import { getSecurityActions } from '../../api/threats.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type Theme = ReturnType<typeof useC>;

export const SECURITY_ACTIONS_PAGE_SIZE = 25;

/** Founder bug 2026-10-20 Report 1: the trail used to render 25 rows
 *  on first paint even when the merchant only wanted a quick glance.
 *  Collapsed default shows the newest 10; "Show all N" expands to the
 *  full paginated view (25/page, server-paginated through the same
 *  Pager pattern AddressTables uses). */
export const SECURITY_ACTIONS_COLLAPSED = 10;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(d);
}

/**
 * Plain-English rendering of a {@link SecurityActionEntry.kind}. The
 * component is UI-only; copy stays within Sill's voice (precise,
 * technical, no exclamation points, no emoji).
 */
function describeKind(entry: SecurityActionEntry): string {
  switch (entry.kind) {
    case 'block_added':
      return entry.detail === null
        ? `Blocked ${entry.target_display}`
        : `Blocked ${entry.target_display} ${entry.detail}`;
    case 'block_removed':
      return `Unblocked ${entry.target_display}`;
    case 'trusted_added':
      return `Trusted ${entry.target_display}`;
    case 'trusted_removed':
      return `Removed ${entry.target_display} from the trusted list`;
    case 'blocking_mode_on':
      return 'Turned blocking on';
    case 'blocking_mode_off':
      return 'Turned blocking off';
    case 'attack_paths_mode_on':
      return 'Turned attack paths on';
    case 'attack_paths_mode_off':
      return 'Turned attack paths off';
    case 'attack_paths_custom_set':
      return entry.detail === null
        ? 'Updated the custom attack paths list'
        : `Updated the custom attack paths list to ${entry.detail}`;
    case 'attack_paths_exceptions_set':
      return entry.detail === null
        ? 'Updated the attack paths exceptions list'
        : `Updated the attack paths exceptions list to ${entry.detail}`;
    case 'campaign_autoblock_armed':
      return entry.detail === null
        ? `Armed campaign auto-block for ${entry.target_display}`
        : `Armed campaign auto-block for ${entry.target_display} and ${entry.detail}`;
    case 'campaign_autoblock_disarmed':
      return entry.detail === null
        ? `Undid campaign auto-block for ${entry.target_display}`
        : `Undid campaign auto-block for ${entry.target_display} and ${entry.detail}`;
    case 'campaign_autoblock_cap_reached':
      return `Campaign auto-block for ${entry.target_display} ${entry.detail ?? 'reached its cap'}`;
    case 'threat_ignored':
      return `Ignored alerts from ${entry.target_display}`;
    case 'sender_ip_viewed':
      return `Revealed the address behind ${entry.target_display}`;
  }
}

function headCell(C: Theme): React.CSSProperties {
  return {
    textAlign: 'left', fontFamily: monoFont, fontSize: 9, letterSpacing: 2, fontWeight: 400,
    color: C.muted, padding: '8px 10px 8px 0', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
  };
}

function bodyCell(C: Theme): React.CSSProperties {
  return {
    fontFamily: sansFont, fontSize: 12, color: C.body,
    padding: '8px 10px 8px 0', borderBottom: `1px solid ${C.borderSoft}`, verticalAlign: 'middle',
  };
}

function Pager({ page, pages, start, shown, total, onPage, testId, C }: {
  readonly page: number;
  readonly pages: number;
  readonly start: number;
  readonly shown: number;
  readonly total: number;
  readonly onPage: (p: number) => void;
  readonly testId: string;
  readonly C: Theme;
}): JSX.Element | null {
  if (total <= SECURITY_ACTIONS_PAGE_SIZE) return null;
  const btn = (disabled: boolean): React.CSSProperties => ({
    fontFamily: sansFont, fontSize: 12, padding: '5px 10px', minHeight: 32,
    background: 'transparent', color: disabled ? C.muted : C.text,
    border: `1px solid ${C.border}`, borderRadius: 2,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  });
  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginRight: 'auto' }}>
        {start + 1}&ndash;{start + shown} of {total.toLocaleString('en-US')}
      </span>
      <button type="button" data-testid={`${testId}-prev`} disabled={page === 0} onClick={() => onPage(page - 1)} style={btn(page === 0)}>
        Previous
      </button>
      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
        Page {page + 1} of {pages}
      </span>
      <button type="button" data-testid={`${testId}-next`} disabled={page >= pages - 1} onClick={() => onPage(page + 1)} style={btn(page >= pages - 1)}>
        Next
      </button>
    </div>
  );
}

export interface SecurityActionsTableProps {
  readonly site_id: string;
  /** Optional seed state used by tests; production uses the HTTP client. */
  readonly initial?: SecurityActionsListResponse;
}

/**
 * Fetches the trail from the API and renders a compact
 * When / Who / What table. All server strings are rendered as plain
 * text — no innerHTML, no HTML interpolation.
 */
export function SecurityActionsTable({ site_id, initial }: SecurityActionsTableProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [state, setState] = useState<SecurityActionsListResponse | null>(initial ?? null);
  const [page, setPage] = useState<number>((initial?.page ?? 1) - 1);
  const [loading, setLoading] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);

  const refetch = useCallback(async (next_page: number) => {
    if (http === null) return;
    setLoading(true);
    try {
      const res = await getSecurityActions(http, site_id, next_page + 1);
      setState(res);
    } catch {
      // Keep prior state on a flap; the enclosing card's error banner
      // handles unauthenticated / forbidden surfaces (same posture as
      // AddressTables).
    } finally {
      setLoading(false);
    }
  }, [http, site_id]);

  useEffect(() => {
    if (initial !== undefined) return;
    void refetch(0);
  }, [initial, refetch]);

  const onPage = useCallback((p: number) => {
    setPage(p);
    void refetch(p);
  }, [refetch]);

  const total = state?.total_count ?? 0;
  const start = page * SECURITY_ACTIONS_PAGE_SIZE;
  const pages = Math.max(1, Math.ceil(total / SECURITY_ACTIONS_PAGE_SIZE));
  // Collapsed view: just the first `SECURITY_ACTIONS_COLLAPSED` rows from
  // the first server page. The expand control is only shown when the
  // server reports more than that count; everything else is deferred
  // until the merchant actually asks for it.
  const visibleRows = expanded
    ? (state?.actions ?? [])
    : (state?.actions ?? []).slice(0, SECURITY_ACTIONS_COLLAPSED);

  return (
    <div data-testid="security-actions-table">
      <div style={{
        fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted,
        marginBottom: 4,
      }}>
        RECENT CHANGES {state === null ? '' : `· ${total.toLocaleString('en-US')}`}
      </div>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>
        Every security change on this site — who changed what, and when.
      </div>
      {state === null ? (
        <div
          data-testid="security-actions-loading"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, padding: '8px 0' }}
        >
          Loading…
        </div>
      ) : state.actions.length === 0 ? (
        <div
          data-testid="security-actions-empty"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, padding: '8px 0' }}
        >
          No changes yet.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 440, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th scope="col" style={headCell(C)}>WHEN</th>
                  <th scope="col" style={headCell(C)}>WHO</th>
                  <th scope="col" style={headCell(C)}>WHAT</th>
                </tr>
              </thead>
              <tbody data-testid="security-actions-list">
                {visibleRows.map((entry) => (
                  <tr key={entry.action_id} data-testid="security-actions-row" data-kind={entry.kind}>
                    <td style={{ ...bodyCell(C), whiteSpace: 'nowrap' }} data-testid="security-actions-when">
                      {formatWhen(entry.at)}
                    </td>
                    <td style={bodyCell(C)} data-testid="security-actions-who">
                      {entry.actor_display}
                    </td>
                    <td
                      style={{ ...bodyCell(C), color: C.text, overflowWrap: 'anywhere' }}
                      data-testid="security-actions-what"
                    >
                      {describeKind(entry)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {expanded ? (
            <Pager
              page={page}
              pages={pages}
              start={start}
              shown={state.actions.length}
              total={total}
              onPage={onPage}
              testId="security-actions-pager"
              C={C}
            />
          ) : total > SECURITY_ACTIONS_COLLAPSED ? (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                data-testid="security-actions-show-all"
                onClick={() => setExpanded(true)}
                style={{
                  fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5,
                  color: C.limeFg,
                  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                }}
              >
                Show all {total.toLocaleString('en-US')} changes
              </button>
            </div>
          ) : null}
          {loading ? (
            <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 6 }}>
              Loading…
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
